import axios from 'axios';
import * as React from 'react';
import { enqueueSnackbar } from 'notistack';
import { useAuth0 } from '@auth0/auth0-react';
import { useState, useEffect, useContext } from 'react';

import Stack from '@mui/material/Stack';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Toolbar from '@mui/material/Toolbar';
import Collapse from '@mui/material/Collapse';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete';

import { useOffSetTop } from 'src/hooks/use-off-set-top';
import { useResponsive } from 'src/hooks/use-responsive';

import { bgBlur } from 'src/theme/css';

import Logo from 'src/components/logo';
import SvgColor from 'src/components/svg-color';
import { useSettingsContext } from 'src/components/settings';

import { NAV, HEADER } from '../config-layout';
import { useRouter } from '../../routes/hooks';
import { useBoolean } from '../../hooks/use-boolean';
import { AuthContext } from '../../auth/context/auth0';
import AccountPopover from '../common/account-popover';
import { ResponseErrorChecker } from '../../universal-components/responseErrorChecker';
import { useDjangoApi } from '../../universal-components/djangoServerUtilities/djangoHandler';

type Props = {
  onOpenNav?: VoidFunction;
};

export default function Header({ onOpenNav }: Props) {
  const theme = useTheme();
  const djangoApi = useDjangoApi();
  const { user, logout } = useAuth0();
  const { replace } = useRouter();
  const { currentUser, user: auth0User } = useContext(AuthContext);
  const settings = useSettingsContext();
  const spoofingQuickView = useBoolean();
  const router = useRouter();

  const isNavHorizontal = settings.themeLayout === 'horizontal';

  const isNavMini = settings.themeLayout === 'mini';

  const lgUp = useResponsive('up', 'lg');

  const offset = useOffSetTop(HEADER.H_DESKTOP);

  const offsetTop = offset && !isNavHorizontal;

  const [djangoUsers, setDjangoUsers] = useState<{ id: string, email: string }[]>([])
  const [springbootProviders, setSpringbootProviders] = useState<string[]>([])
  const springbootHasLoaded = useBoolean(false);
  const djangoHasLoaded = useBoolean(false);

  const fetchSpringbootProviders = async () => {
    try {
      const response = await ResponseErrorChecker(() => axios.get(`${import.meta.env.VITE_API_URL}/providers/admin/emails`), logout, replace);
      if (response && response.status === 200) {
        setSpringbootProviders(response.data);
        springbootHasLoaded.onTrue();
      }
    } catch (error) {
      console.error('There was an error retrieving the provider data', error);
    }
  };

  const fetchDjangoUsers = async () => {
    try {
      const response = await djangoApi.get('/auth/spoofing/get-spoofable-users/');
      if (response && response.status === 200) {
        setDjangoUsers(response.data);
        djangoHasLoaded.onTrue();
      }
    } catch (error) {
      console.error('There was an error retrieving the provider data', error);
    }
  };

  useEffect(() => {
    if (user && (user.email === "oleg.pylnev@unifymentalhealth.com" || user.email === "john.stevens@unifymentalhealth.com" || user.email === "kevin.lehtinen@unifymentalhealth.com" || user.email === "justin.zhu@unifymentalhealth.com")) {
      fetchSpringbootProviders()
      fetchDjangoUsers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const spoofSpringbootProvider = async (providerEmail: String) => {
    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/auth/spoof-provider`, { providerEmail });
      if (response.status === 200) {
        router.reload()
      } else {
        enqueueSnackbar("Unable to spoof Springboot provider", { variant: 'error' })
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const resetSpoofing = () => {
    djangoApi.removeSpoofingToken()
    spoofSpringbootProvider('robot@meridian-counseling.com')
      .then(() => { router.reload() })
  }

  const renderContent = (
    <>
      {lgUp && isNavHorizontal && <Logo sx={{ mr: 2.5 }} />}

      {!lgUp && (
        <IconButton onClick={onOpenNav}>
          <SvgColor src="/assets/icons/navbar/ic_menu_item.svg" />
        </IconButton>
      )}
      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1 }}
      >
        {(user && (user.email === "oleg.pylnev@unifymentalhealth.com" || user.email === "john.stevens@unifymentalhealth.com" || user.email === "kevin.lehtinen@unifymentalhealth.com" || user.email === "justin.zhu@unifymentalhealth.com")) && (
          <>
            <Stack sx={{ outline: '1px solid lightgrey', px: 2, py: .5, borderRadius: .75 }}>
              <Typography fontSize={14}>Springboot: {currentUser?.email ?? user?.email}</Typography>
              <Typography fontSize={14}>Django: {auth0User?.email}</Typography>
            </Stack>
            <Collapse
              orientation='horizontal'
              in={spoofingQuickView.value}
              easing='linear'
            >

              <Stack
                flexGrow={1}
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={{ xs: 0.5, sm: 1 }}
              >
                {springbootHasLoaded.value ?
                  <FormControl sx={{ width: '300px' }}>
                    <Autocomplete
                      options={springbootProviders}
                      // getOptionLabel={(option) => option || ''}  // Use 'email' as the display field
                      onChange={(event, newValue) => {
                        if (newValue) {
                          spoofSpringbootProvider(newValue);
                        }

                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Spoof springboot user"
                          variant="outlined"
                        />
                      )}
                      sx={{
                        maxHeight: 400,
                        overflow: 'auto',
                        paddingY: 1
                      }}
                    />
                  </FormControl>
                  :
                  <Skeleton sx={{ width: '300px', height: '50px' }} />
                }
                {djangoHasLoaded.value ?
                  <FormControl sx={{ width: '300px' }}>
                    <Autocomplete
                      options={djangoUsers}
                      getOptionLabel={(option) => option.email || ''}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          djangoApi.createSpoofingToken(newValue.id);
                        }

                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Spoof django user"
                          variant="outlined"
                        />
                      )}
                      sx={{
                        maxHeight: 400,
                        overflow: 'auto',
                        paddingY: 1
                      }}
                    />
                  </FormControl>
                  :
                  <Skeleton sx={{ width: '300px', height: '50px' }} />
                }
                <Button variant='contained' onClick={resetSpoofing}>
                  Reset
                </Button>
              </Stack>
            </Collapse>
            <Button onClick={spoofingQuickView.onToggle} variant='soft'>
              Spoofing
            </Button>
          </>
        )}
        <AccountPopover />
      </Stack>
    </>
  );

  return (
    <AppBar
      sx={{
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(lgUp && {
          width: `calc(100% - ${NAV.W_VERTICAL + 1}px)`,
          height: HEADER.H_DESKTOP,
          ...(offsetTop && {
            height: HEADER.H_DESKTOP_OFFSET,
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: 'background.default',
            height: HEADER.H_DESKTOP_OFFSET,
            borderBottom: `dashed 1px ${theme.palette.divider}`,
          }),
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_MINI + 1}px)`,
          }),
        }),
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
        {renderContent}
      </Toolbar>
    </AppBar>
  );
}
